import { useMemo } from "react";
import BoardRunnerCell from "../VerticalSchedular/components/BoradRunnerCell";
import {
  CellsWrapper,
  Events,
  ResourceHeader,
  TimeSlotsWrapper,
  TouchDndPreview,
} from "./components";
import "./styles.scss";
import useSchedular from "./useSchedular";

const Schedular = ({
  events: unprocessedEvents,
  resources,
  handleDropFromOutsideCb,
  handleDropOnEventCb,
  handleInnerDropCb,
  handleResizeEventCb,
  handleResourceDropCb,
  calendarDate,
  components,
  handleSelectSlot,
  handleSelectEvent,
  timeInterval = 60,
  allowSlotOverlap = false,
  timezone,
  startHourInView,
  autoScroll = false,
  allowDropOnSchedular,
  providerProps,
  handleUpdateRooms,
  isDropDownActive,
  facilityId,
  isBoardRunner,
  selectedFacility,
  showAssignmentsBar,
  handleRefreshProvidersList
}) => {
  const {
    timeSlots,
    handleCellRef,
    handleColumnRef,
    handleTimeSlotRef,
    handleOverlayDrop,
    getDroppedEventStyles,
    isCalendarRendered,
    setIsCalendarRendered,
    setPreviousCalendarScrollOffset,
    handleResizeEvent,
    events,
    isTouchDevice,
    calendarContainerRef,
    boardRunnerDropRef,
    boardRunners,
    handleAssignBoardRunner,
    handleUnassignBoardRunner,
    getBoardRunnersApiLoading,
    unassignBoardRunnerApiLoading,
    renderProviderSelectDropdown,
    boardAssignments,
    getBoardAssignmentsApiLoading,
    handleAssignBoardAssignment,
    handleUnassignBoardAssignment,
    assignBoardAssignmentApiLoading,
    unassignBoardAssignmentApiLoading,
    useAssignmentDrop,
    isProvider
  } = useSchedular({
    resources,
    unprocessedEvents,
    handleDropFromOutsideCb,
    handleDropOnEventCb,
    handleInnerDropCb,
    handleResizeEventCb,
    calendarDate,
    allowSlotOverlap,
    timeInterval,
    timezone,
    startHourInView,
    autoScroll,
    boardDate: calendarDate,
    facilityId,
    handleRefreshProvidersList
  });

  // Override --column-min-width on the schedular root so header, boardrunner,
  // and grid all consume the exact same value — guarantees alignment on every
  // screen size.
  const dynamicColumnWidth = useMemo(() => {
    const w = window.innerWidth;
    const maxNameLen = Math.max(...resources.map((r) => (r.name || "").length), 0);
    if (w > 768) {
      // Desktop: account for badge (~20px) + padding (16px) + gap (6px) + select (40px)
      // + Montserrat semibold at ~12px/char. Base matches CSS breakpoints.
      const basePx = w <= 992 ? 150 : 170;
      return Math.max(basePx, maxNameLen * 12 + 82);
    }
    // Mobile formula unchanged — was working correctly
    const basePx = w <= 576 ? 110 : 130;
    return Math.max(basePx, maxNameLen * 10 + 66);
  }, [resources]);

  return (
    <div
      className="schedular"
      ref={calendarContainerRef}
      style={dynamicColumnWidth ? { "--column-min-width": `${dynamicColumnWidth}px` } : undefined}
    >
      <ResourceHeader
        resources={resources}
        customComponent={components?.resourceHeader}
        allowDropOnSchedular={allowDropOnSchedular}
        handleResourceDropCb={handleResourceDropCb}
        providers={providerProps.providers}
        handleUpdateRooms={handleUpdateRooms}
        isDropDownActive={isDropDownActive}
      />
      {showAssignmentsBar && (
        <div className="schedular-boardrunner-wrapper">
          <div className="boardrunner-gutter" />
          <div className="boardrunner-inner">
            <div className="boardrunner-spacer">
              {resources.map((resource) => (
                <div key={resource.id} className="boardrunner-column-spacer" />
              ))}
            </div>
            <div className="boardrunner-content-overlay">
              <BoardRunnerCell
                isNormal={true}
                providers={providerProps?.providers}
                dropRef={boardRunnerDropRef}
                runner={boardRunners?.[0]}
                handleUnassignBoardRunner={handleUnassignBoardRunner}
                getBoardRunnersApiLoading={getBoardRunnersApiLoading}
                unassignBoardRunnerApiLoading={unassignBoardRunnerApiLoading}
                renderProviderSelectDropdown={renderProviderSelectDropdown}
                assignments={boardAssignments}
                getBoardAssignmentsApiLoading={getBoardAssignmentsApiLoading}
                handleAssignBoardAssignment={handleAssignBoardAssignment}
                handleUnassignBoardAssignment={handleUnassignBoardAssignment}
                assignBoardAssignmentApiLoading={assignBoardAssignmentApiLoading}
                unassignBoardAssignmentApiLoading={unassignBoardAssignmentApiLoading}
                assignmentDrop={useAssignmentDrop}
                selectedFacility={selectedFacility}
                isBoardRunner={isBoardRunner}
                isProvider={isProvider}
              />
            </div>
          </div>
        </div>
      )}
      <div className="schedular-content">
        {isTouchDevice && <TouchDndPreview />}
        <div className="schedular-content-inner">
          <TimeSlotsWrapper
            timeSlots={timeSlots}
            handleTimeSlotRef={handleTimeSlotRef}
          />
          <CellsWrapper
            events={events}
            resources={resources}
            timeSlots={timeSlots}
            handleCellRef={handleCellRef}
            handleColumnRef={handleColumnRef}
            setIsCalendarRendered={setIsCalendarRendered}
            isCalendarRendered={isCalendarRendered}
            handleOverlayDrop={handleOverlayDrop}
            handleSelectSlot={handleSelectSlot}
            allowDropOnSchedular={allowDropOnSchedular}>
            {isCalendarRendered && (
              <Events
                resources={resources}
                customComponent={components?.event}
                events={events}
                getDroppedEventStyles={getDroppedEventStyles}
                setPreviousCalendarScrollOffset={
                  setPreviousCalendarScrollOffset
                }
                handleDropOnEventCb={handleDropOnEventCb}
                handleResizeEvent={handleResizeEvent}
                handleSelectEvent={handleSelectEvent}
              />
            )}
          </CellsWrapper>
        </div>
      </div>
    </div>
  );
};

export default Schedular;
